import { useEffect, useState } from 'react';
import { X, Loader2, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCreateMedicalLabMutation, useUpdateMedicalLabMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { MedicalLab, CreateMedicalLabRequest } from '@/types';

interface MedicalLabFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Present → edit mode; absent → create mode.
    lab?: MedicalLab | null;
}

type FormState = {
    name: string;
    address: string;
    brand: string;
    partnerName: string;
    area: string;
    city: string;
    state: string;
    region: string;
    isActive: boolean;
};

function emptyForm(): FormState {
    return {
        name: '',
        address: '',
        brand: '',
        partnerName: '',
        area: '',
        city: '',
        state: '',
        region: '',
        isActive: true,
    };
}

function fromLab(lab: MedicalLab): FormState {
    return {
        name: lab.name ?? '',
        address: lab.address ?? '',
        brand: lab.brand ?? '',
        partnerName: lab.partnerName ?? '',
        area: lab.area ?? '',
        city: lab.city ?? '',
        state: lab.state ?? '',
        region: lab.region ?? '',
        isActive: lab.isActive,
    };
}

const TEXT_FIELDS: { key: keyof FormState; label: string; required?: boolean; placeholder?: string }[] = [
    { key: 'name', label: 'Name', required: true, placeholder: 'BeaconHealth Diagnostics — Lekki Phase 1' },
    { key: 'address', label: 'Address', required: true, placeholder: '70 Fatai Arobieke Street, Lekki Phase 1' },
    { key: 'brand', label: 'Brand', placeholder: 'BeaconHealth Diagnostics' },
    { key: 'partnerName', label: 'Partner name', placeholder: 'e.g. Ashmed Specialist Hospital' },
    { key: 'area', label: 'Area', placeholder: 'Lekki Phase 1' },
    { key: 'city', label: 'City', placeholder: 'Lekki' },
    { key: 'state', label: 'State', placeholder: 'Lagos' },
    { key: 'region', label: 'Region', placeholder: 'Lagos State' },
];

export default function MedicalLabFormModal({ isOpen, onClose, lab }: MedicalLabFormModalProps) {
    const [form, setForm] = useState<FormState>(() => emptyForm());
    const [error, setError] = useState('');
    const [createLab, { isLoading: isCreating }] = useCreateMedicalLabMutation();
    const [updateLab, { isLoading: isUpdating }] = useUpdateMedicalLabMutation();
    const isSaving = isCreating || isUpdating;

    useEffect(() => {
        if (isOpen) {
            setForm(lab ? fromLab(lab) : emptyForm());
            setError('');
        }
    }, [isOpen, lab]);

    const setField = (key: keyof FormState, value: string | boolean) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async () => {
        setError('');
        if (!form.name.trim() || !form.address.trim()) {
            setError('Name and address are required.');
            return;
        }
        // Send every field; optional ones are omitted when blank.
        const payload: CreateMedicalLabRequest = {
            name: form.name.trim(),
            address: form.address.trim(),
            isActive: form.isActive,
            ...(form.brand.trim() ? { brand: form.brand.trim() } : {}),
            ...(form.partnerName.trim() ? { partnerName: form.partnerName.trim() } : {}),
            ...(form.area.trim() ? { area: form.area.trim() } : {}),
            ...(form.city.trim() ? { city: form.city.trim() } : {}),
            ...(form.state.trim() ? { state: form.state.trim() } : {}),
            ...(form.region.trim() ? { region: form.region.trim() } : {}),
        };
        try {
            if (lab) {
                await updateLab({ id: lab.id, data: payload }).unwrap();
            } else {
                await createLab(payload).unwrap();
            }
            onClose();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to save the lab.'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="ml-auto h-full w-full md:max-w-md bg-white shadow-2xl z-10 flex flex-col animate-in slide-in-from-right duration-300">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                            <FlaskConical className="w-5 h-5 text-[#FF6B35]" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{lab ? 'Edit lab' : 'Add lab'}</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Name and address are required.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {TEXT_FIELDS.map((field) => (
                            <div key={field.key}>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                <input
                                    type="text"
                                    value={form[field.key] as string}
                                    onChange={(e) => setField(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35] transition-all"
                                />
                            </div>
                        ))}
                        <label className="flex items-center gap-2.5 cursor-pointer pt-1">
                            <input
                                type="checkbox"
                                checked={form.isActive}
                                onChange={(e) => setField('isActive', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-[#FF6B35] focus:ring-[#FF6B35]"
                            />
                            <span className="text-sm text-gray-700">Active (visible in CS / medical pickers)</span>
                        </label>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="outline"
                            className="flex-1 py-2.5 rounded-lg border-gray-300 hover:bg-gray-100"
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white py-2.5 rounded-lg font-medium"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="animate-spin h-4 w-4" />
                                    Saving...
                                </span>
                            ) : lab ? (
                                'Save changes'
                            ) : (
                                'Create lab'
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
